import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminRole } from '@/lib/auth';

const USE_COOKIES = import.meta.env.VITE_ADMIN_AUTH_COOKIES === 'true';

export interface AdminSession {
  userId: string;
  email: string;
  fullName: string;
  role: AdminRole;
  twoFactorEnabled: boolean;
  // Legacy Bearer fields — only populated when VITE_ADMIN_AUTH_COOKIES !== 'true'.
  // In cookie mode these stay undefined; tokens live in HttpOnly cookies on the backend.
  token?: string;
  refreshToken?: string;
  tokenExpiresAt?: number;
}

interface AuthState {
  session: AdminSession | null;
  /** 2FA intermediate state — NOT persisted */
  requires2FA: boolean;
  /** Temp token for the 2FA step — NOT persisted */
  tempToken: string | null;
  /** User ID during the 2FA step — NOT persisted */
  pendingUserId: string | null;

  setSession: (s: AdminSession | null) => void;
  startTwoFactorStep: (params: { userId: string; tempToken: string }) => void;
  clearTwoFactorStep: () => void;
  logout: () => void;
  canAccess: (minRole: AdminRole) => boolean;
}

const ROLE_LEVEL: Record<AdminRole, number> = {
  superadmin: 2,
  moderator: 1,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      requires2FA: false,
      tempToken: null,
      pendingUserId: null,

      setSession: (session) => {
        if (!USE_COOKIES) {
          if (session?.token) {
            localStorage.setItem('admin_token', session.token);
            localStorage.setItem('admin_user_id', session.userId);
          } else {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user_id');
          }
        }
        set({ session, requires2FA: false, tempToken: null, pendingUserId: null });
      },

      startTwoFactorStep: ({ userId, tempToken }) => {
        set({ requires2FA: true, tempToken, pendingUserId: userId });
      },

      clearTwoFactorStep: () => {
        set({ requires2FA: false, tempToken: null, pendingUserId: null });
      },

      logout: () => {
        if (!USE_COOKIES) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user_id');
        }
        set({
          session: null,
          requires2FA: false,
          tempToken: null,
          pendingUserId: null,
        });
      },

      canAccess: (minRole: AdminRole): boolean => {
        const session = get().session;
        if (!session) return false;
        return ROLE_LEVEL[session.role] >= ROLE_LEVEL[minRole];
      },
    }),
    {
      name: 'ouropix-admin-auth',
      partialize: (state) => ({
        // Cookie mode: persist only non-sensitive metadata (no tokens).
        // Legacy mode: persist full session (tokens included for fallback compatibility).
        session: state.session
          ? USE_COOKIES
            ? {
                userId: state.session.userId,
                email: state.session.email,
                fullName: state.session.fullName,
                role: state.session.role,
                twoFactorEnabled: state.session.twoFactorEnabled,
              }
            : state.session
          : null,
      }),
    },
  ),
);
